/**
 * Preset dashboardMeta helpers — categories, folder order, and component sorting.
 * Layout fields live in version dashboardMeta; presets store them alongside gameFlow etc.
 * Old presets without these fields keep working via read-time fallbacks in the UI.
 */

import _ from "lodash";
import {
  parseCategoryPath,
  resolveTopLevelCategoryFolderOrder,
} from "./playEngineComponentCategoryUtils";
import { resolveRootLayoutOrder } from "./playEngineComponentOrderUtils";

/** dashboardMeta keys owned by Version Settings layout (not engine). */
export const PRESET_LAYOUT_META_KEYS = [
  "componentCategories",
  "componentCategoryFolders",
  "componentCategoryChildOrder",
  "rootLayoutOrder",
  "componentOrder",
];

/** @param {object | null | undefined} playEngineConfig */
export function getPlayEngineComponentIds(playEngineConfig) {
  const comps = playEngineConfig?.components;
  if (!Array.isArray(comps)) return new Set();
  return new Set(comps.map((c) => String(c.id)).filter(Boolean));
}

/** @param {object | null | undefined} dashboardMeta */
export function extractLayoutDashboardMeta(dashboardMeta) {
  const src = dashboardMeta || {};
  const out = {};
  for (const key of PRESET_LAYOUT_META_KEYS) {
    if (src[key] != null) out[key] = _.cloneDeep(src[key]);
  }
  return out;
}

/**
 * Folder names that are valid siblings under parentPath (from assignments + stored order).
 *
 * @param {string} parentPath
 * @param {Record<string, string>} categories
 * @param {string[]} topFolders
 * @param {Record<string, string[]>} childOrder
 */
function getChildFolderNamesAt(parentPath, categories, topFolders, childOrder) {
  const names = new Set();
  const parentSegs = parseCategoryPath(parentPath);

  Object.values(categories).forEach((catPath) => {
    const segs = parseCategoryPath(catPath);
    if (segs.length <= parentSegs.length) return;
    if (!parentSegs.every((seg, i) => segs[i] === seg)) return;
    names.add(segs[parentSegs.length]);
  });

  (childOrder[parentPath] || []).forEach((name) => names.add(name));

  if (!parentPath) {
    topFolders.forEach((name) => names.add(name));
  }
  return names;
}

/**
 * Prune layout meta to component ids / folder names that exist in playEngineConfig.
 * Missing layout fields are omitted — callers rely on UI fallbacks for old presets.
 *
 * @param {object | null | undefined} layoutMeta
 * @param {object | null | undefined} playEngineConfig
 */
export function sanitizeLayoutDashboardMeta(layoutMeta, playEngineConfig) {
  const validIds = getPlayEngineComponentIds(playEngineConfig);
  const src = layoutMeta || {};

  const categories = {};
  Object.entries(src.componentCategories || {}).forEach(([id, path]) => {
    const compId = String(id);
    if (!validIds.has(compId)) return;
    if (typeof path !== "string" || !path.trim()) return;
    categories[compId] = path.trim();
  });

  const folders = resolveTopLevelCategoryFolderOrder(categories, src.componentCategoryFolders);
  const rawChild = src.componentCategoryChildOrder || {};

  const childOrder = {};
  Object.entries(rawChild).forEach(([parentPath, names]) => {
    if (!Array.isArray(names)) return;
    const validNames = getChildFolderNamesAt(parentPath, categories, folders, rawChild);
    const filtered = names.filter((name) => validNames.has(name));
    if (filtered.length > 0) childOrder[parentPath] = filtered;
  });

  const order = {};
  Object.entries(src.componentOrder || {}).forEach(([key, ids]) => {
    if (!Array.isArray(ids)) return;
    if (key.startsWith("categoryFolder:")) {
      const parentPath = key === "categoryFolder:root"
        ? ""
        : key.slice("categoryFolder:".length);
      const validNames = getChildFolderNamesAt(parentPath, categories, folders, rawChild);
      const filtered = ids.filter((name) => validNames.has(name));
      if (filtered.length > 0) order[key] = filtered;
      return;
    }
    const filtered = ids.filter((id) => validIds.has(String(id)));
    if (filtered.length > 0) order[key] = filtered;
  });

  const uncategorizedIds = [...validIds].filter((id) => !categories[id]);
  const rootLayoutOrder = resolveRootLayoutOrder(
    src.rootLayoutOrder,
    uncategorizedIds,
    folders,
    order.root
  );

  const result = {};
  if (Object.keys(categories).length > 0) result.componentCategories = categories;
  if (folders.length > 0) result.componentCategoryFolders = folders;
  if (Object.keys(childOrder).length > 0) result.componentCategoryChildOrder = childOrder;
  if (rootLayoutOrder.length > 0) result.rootLayoutOrder = rootLayoutOrder;
  if (Object.keys(order).length > 0) result.componentOrder = order;
  return result;
}

/**
 * dashboardMeta payload for preset save — keeps agent/other fields, sanitizes layout.
 *
 * @param {{ versionDashboardMeta?: object, playEngineConfig?: object }} params
 */
export function buildPresetDashboardMeta({ versionDashboardMeta, playEngineConfig }) {
  const versionMeta = versionDashboardMeta || {};
  const layout = sanitizeLayoutDashboardMeta(
    extractLayoutDashboardMeta(versionMeta),
    playEngineConfig
  );
  const other = _.omit(versionMeta, PRESET_LAYOUT_META_KEYS);
  return { ...other, ...layout };
}

/**
 * dashboardMeta to apply when loading a preset — same sanitization against preset components.
 *
 * @param {{ presetDashboardMeta?: object, playEngineConfig?: object }} params
 */
export function resolvePresetDashboardMeta({ presetDashboardMeta, playEngineConfig }) {
  const presetMeta = presetDashboardMeta || {};
  const layout = sanitizeLayoutDashboardMeta(
    extractLayoutDashboardMeta(presetMeta),
    playEngineConfig
  );
  const other = _.omit(presetMeta, PRESET_LAYOUT_META_KEYS);
  return { ...other, ...layout };
}
